import { useEffect, useRef, useState } from "react";
import VoiceService, {
    VoiceMessage,
} from "../../services/voiceService";
import useMicrophone from "../../hooks/useMicrophone";
import MentiAvatar from "../../components/MentiAvatar/MentiAvatar";

interface ChatMessage {
    role: "user" | "assistant";
    text: string;
}

type Status =
    | "idle"
    | "listening"
    | "processing"
    | "speaking";
    
export default function VoiceChat() {
    const { start, stop } = useMicrophone();
    const [messages, setMessages] =
        useState<ChatMessage[]>([]);
    const [status, setStatus] =
        useState<Status>("idle");
    const [isActive, setIsActive] =
        useState(false);
    const audioRef =
        useRef<HTMLAudioElement | null>(null);
    /*
     * Keep the latest active state available
     * inside VoiceService callbacks.
     */
    const isActiveRef =
        useRef(false);
    /*
     * Keep track of the current audio URL
     * so we can release it later.
     */
    const audioUrlRef =
        useRef<string | null>(null);
    /*
     * VoiceService
     */
    useEffect(() => {
        const handleMessage = (
            message: VoiceMessage
        ) => {
            switch (message.type) {
                /*
                 * User speech converted to text
                 */
                case "transcript":
                    if (message.text) {
                        setMessages(prev => [
                            ...prev,
                            {
                                role: "user",
                                text: message.text!,
                            },
                        ]);
                        setStatus("processing");
                    }
                    break;
                /*
                 * LLM response
                 */
                case "response":
                    if (message.text) {
                        setMessages(prev => [
                            ...prev,
                            {
                                role: "assistant",
                                text: message.text!,
                            },
                        ]);
                    }
                    break;
                /*
                 * Edge TTS started
                 */
                case "speaking_start":
                    setStatus("speaking");
                    break;
                /*
                 * Edge TTS finished
                 */
                case "speaking_end":
                    setStatus(
                        isActiveRef.current
                            ? "listening"
                            : "idle"
                    );
                    break;
            }
        };
        /*
         * Receive TTS audio
         */
        const handleAudio = (
            audio: ArrayBuffer
        ) => {
            const blob =
                new Blob(
                    [audio],
                    {
                        type: "audio/mpeg",
                    }
                );
            const url =
                URL.createObjectURL(blob);
            /*
             * Clean previous audio URL
             */
            if (audioUrlRef.current) {
                URL.revokeObjectURL(
                    audioUrlRef.current
                );
            }
            audioUrlRef.current = url;
            const audioElement =
                audioRef.current;
            if (!audioElement) {
                return;
            }
            /*
             * Set new TTS audio
             */
            audioElement.src = url;
            /*
             * Start playing
             */
            audioElement
                .play()
                .catch(error => {
                    console.error(
                        "Menti audio playback error:",
                        error
                    );
                });
        };
        /*
         * Connect VoiceService
         */
        VoiceService.connect(
            handleMessage,
            handleAudio
        );
        /*
         * Cleanup
         */
        return () => {
            VoiceService.disconnect();
            if (audioUrlRef.current) {
                URL.revokeObjectURL(
                    audioUrlRef.current
                );
                audioUrlRef.current = null;
            }
        };
    }, []);
    /*
     * Start voice chat
     */
    const handleStart = async () => {
        try {
            await start();
            isActiveRef.current = true;
            setIsActive(true);
            setStatus("listening");
        } catch (error) {
            console.error(
                "Unable to start microphone:",
                error
            );
            setStatus("idle");
        }
    };
    /*
     * Stop voice chat
     */
    const handleStop = async () => {
        try {
            await stop();
        } catch (error) {
            console.error(
                "Unable to stop microphone:",
                error
            );
        }
        isActiveRef.current = false;
        setIsActive(false);
        setStatus("idle");
        /*
         * Stop any currently playing TTS
         */
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.removeAttribute(
                "src"
            );
            audioRef.current.load();
        }
    };
    /*
     * When audio naturally finishes
     */
    const handleAudioEnded = () => {
        if (isActiveRef.current) {
            setStatus("listening");
        } else {
            setStatus("idle");
        }
    };
    return (
        <div
            style={{
                maxWidth: 900,
                margin: "0 auto",
                padding: 16,
            }}
        >
            {/* =====================================
                MENTI AVATAR
            ====================================== */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 20,
                }}
            >
                <MentiAvatar
                    audioElement={
                        audioRef.current
                    }
                    status={status}
                />
            </div>
            {/* =====================================
                VOICE CONTROLS
            ====================================== */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                <button
                    onClick={handleStart}
                    disabled={isActive}
                >
                    Start
                </button>
                <button
                    onClick={handleStop}
                    disabled={!isActive}
                >
                    Stop
                </button>
                <span
                    style={{
                        color: "#666",
                        textTransform:
                            "capitalize",
                    }}
                >
                    {status}
                </span>
            </div>
            {/* =====================================
                AUDIO ELEMENT
            ====================================== */}
            <audio
                ref={audioRef}
                hidden
                onEnded={handleAudioEnded}
            />
            {/* =====================================
                CHAT HISTORY
            ====================================== */}
            <div>
                {messages.map(
                    (message, index) => (
                        <div
                            key={index}
                            style={{
                                textAlign:
                                    message.role ===
                                        "user"
                                        ? "right"
                                        : "left",
                                margin: "8px 0",
                            }}
                        >
                            <b>
                                {message.role ===
                                    "user"
                                    ? "You"
                                    : "Menti"}
                            </b>
                            <p
                                style={{
                                    margin:
                                        "2px 0",
                                }}
                            >
                                {message.text}
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}